import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input, Select } from "ui";

import {
  PRODUCT_GENDER_LABELS,
  PRODUCT_SEASON_LABELS,
  PRODUCT_STYLE_LABELS
} from "../../constants";
import {
  EProductGender,
  EProductSeason,
  EProductStyle,
  ProductAttributes,
  UpdateProductAttributesPayload
} from "../../recommendation.types";

import styles from "./ProductAttributesForm.module.scss";

type ColumnField =
  | "gender"
  | "itemType"
  | "material"
  | "style"
  | "season"
  | "dominantColors";

interface Draft {
  columns: Record<ColumnField, string>;
  traits: Record<string, string>;
}

interface ProductAttributesFormProps {
  /** Submitted by the modal's own Save button, which sits outside the form. */
  formId: string;
  attributes?: ProductAttributes;
  /** The attribute set's own fields beyond the shared columns. */
  traitFields: string[];
  vocabularies: Record<string, string[]>;
  onSave: (payload: UpdateProductAttributesPayload) => void;
  onCancel: () => void;
}

// An empty choice clears the attribute — which keeps it empty, since a cleared
// attribute is the admin's too.
const clearableOptions = (options: Array<{ label: string; value: string }>) => [
  { label: "—", value: "" },
  ...options
];

const labelOptions = <T extends string>(labels: Record<T, string>) =>
  clearableOptions(
    (Object.entries(labels) as Array<[T, string]>).map(([value, label]) => ({
      label,
      value
    }))
  );

const GENDER_OPTIONS = labelOptions(PRODUCT_GENDER_LABELS);
const SEASON_OPTIONS = labelOptions(PRODUCT_SEASON_LABELS);
const STYLE_OPTIONS = labelOptions(PRODUCT_STYLE_LABELS);

const toDraft = (
  attributes: ProductAttributes | undefined,
  traitFields: string[]
): Draft => ({
  columns: {
    gender: attributes?.gender ?? "",
    itemType: attributes?.itemType ?? "",
    material: attributes?.material ?? "",
    style: attributes?.style ?? "",
    season: attributes?.season ?? "",
    dominantColors: (attributes?.dominantColors ?? []).join(", ")
  },
  traits: Object.fromEntries(
    traitFields.map((field) => [
      field,
      attributes?.traits?.[field] == null
        ? ""
        : String(attributes.traits[field])
    ])
  )
});

const text = (value: string) => value.trim() || null;

/** Only what changed goes out: anything sent becomes the admin's attribute. */
const toPayload = (
  original: Draft,
  draft: Draft
): UpdateProductAttributesPayload => {
  const payload: UpdateProductAttributesPayload = {};
  const changed = (field: ColumnField) =>
    draft.columns[field].trim() !== original.columns[field].trim();

  if (changed("gender")) {
    payload.gender = (text(draft.columns.gender) as EProductGender) ?? null;
  }
  if (changed("itemType")) {
    payload.itemType = text(draft.columns.itemType);
  }
  if (changed("material")) {
    payload.material = text(draft.columns.material);
  }
  if (changed("style")) {
    payload.style = (text(draft.columns.style) as EProductStyle) ?? null;
  }
  if (changed("season")) {
    payload.season = (text(draft.columns.season) as EProductSeason) ?? null;
  }
  if (changed("dominantColors")) {
    const colors = draft.columns.dominantColors
      .split(",")
      .map((color) => color.trim())
      .filter(Boolean)
      .slice(0, 3);

    payload.dominantColors = colors.length ? colors : null;
  }

  const traits = Object.entries(draft.traits).filter(
    ([field, value]) => value.trim() !== (original.traits[field] ?? "").trim()
  );

  if (traits.length) {
    payload.traits = Object.fromEntries(
      traits.map(([field, value]) => [field, text(value)])
    );
  }

  return payload;
};

const ProductAttributesForm = ({
  formId,
  attributes,
  traitFields,
  vocabularies,
  onSave,
  onCancel
}: ProductAttributesFormProps) => {
  const { t } = useTranslation();

  const [original] = useState(() => toDraft(attributes, traitFields));
  const [draft, setDraft] = useState(original);

  const setColumn = (field: ColumnField, value: string) =>
    setDraft((current) => ({
      ...current,
      columns: { ...current.columns, [field]: value }
    }));

  const setTrait = (field: string, value: string) =>
    setDraft((current) => ({
      ...current,
      traits: { ...current.traits, [field]: value }
    }));

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    const payload = toPayload(original, draft);

    if (Object.keys(payload).length) {
      onSave(payload);
    } else {
      onCancel();
    }
  };

  return (
    <form id={formId} className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.form__grid}>
        <Select
          label={t("Gender")}
          options={GENDER_OPTIONS}
          value={draft.columns.gender}
          onChange={(value) => setColumn("gender", value as string)}
        />
        <Input
          label={t("Item type")}
          value={draft.columns.itemType}
          maxLength={80}
          onChange={(event) => setColumn("itemType", event.target.value)}
        />
        <Input
          label={t("Material")}
          value={draft.columns.material}
          maxLength={80}
          onChange={(event) => setColumn("material", event.target.value)}
        />
        <Select
          label={t("Style")}
          options={STYLE_OPTIONS}
          value={draft.columns.style}
          onChange={(value) => setColumn("style", value as string)}
        />
        <Select
          label={t("Season")}
          options={SEASON_OPTIONS}
          value={draft.columns.season}
          onChange={(value) => setColumn("season", value as string)}
        />
        <Input
          label={t("Dominant colors")}
          placeholder={t("Up to 3, separated by commas")}
          value={draft.columns.dominantColors}
          onChange={(event) => setColumn("dominantColors", event.target.value)}
        />

        {traitFields.map((field) =>
          vocabularies[field] ? (
            <Select
              key={field}
              label={field}
              options={clearableOptions(
                vocabularies[field].map((value) => ({ label: value, value }))
              )}
              value={draft.traits[field] ?? ""}
              onChange={(value) => setTrait(field, value as string)}
            />
          ) : (
            <Input
              key={field}
              label={field}
              value={draft.traits[field] ?? ""}
              maxLength={80}
              onChange={(event) => setTrait(field, event.target.value)}
            />
          )
        )}
      </div>

      <p className={styles.form__hint}>
        {t(
          "What you change here stays as you set it: enrichment will not overwrite it until you revert it to AI."
        )}
      </p>
    </form>
  );
};

export default ProductAttributesForm;
